import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import { type PackageRecord } from "@/lib/billing";

async function fetchCompanyPackage(companyId: string): Promise<PackageRecord | null> {
  // NOTE: user_subscriptions.company_id exists (added 20260514230000_credits_features_models.sql).
  // The cast works around STALE generated types. HUMAN-ACTION: regenerate Supabase
  // types and drop this cast. See REMEDIATION-REPORT.md.
  const { data, error } = await (supabase as any)
    .from("user_subscriptions")
    .select("*, packages(*)")
    .eq("company_id", companyId)
    .in("status", ["trialing", "active"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  
  if (error) throw error;
  if (data?.packages) return data.packages as PackageRecord;

  const fallback = await (supabase as any)
    .from("companies")
    .select("subscription_plan_id, subscription_plans(*)")
    .eq("id", companyId)
    .maybeSingle();
  if (fallback.error) throw fallback.error;
  const plan = fallback.data?.subscription_plans;
  if (!plan) return null;
  return {
    id: plan.id,
    name: plan.name,
    slug: plan.slug,
    description: plan.description ?? null,
    audience: null,
    monthly_price: plan.monthly_price ?? 0,
    currency: "eur",
    is_trial: false,
    is_enterprise: false,
    is_active: true,
    sort_order: 0,
    trial_days: null,
    stripe_product_id: null,
    stripe_price_id: null,
    max_websites: plan.website_limit ?? null,
    max_articles_per_month: plan.article_limit ?? null,
    max_draft_articles: null,
    max_ai_credits: plan.ai_credits ?? null,
    max_media_assets: null,
    max_team_members: plan.team_member_limit ?? null,
    storage_mb: plan.disk_space_mb ?? null,
    bandwidth_mb: null,
    publishing_methods: [],
    support_level: "standard",
    features: plan.features ?? {},
  };
}

export function useCompanyPackageAccess() {
  const { currentCompanyId } = useCompany();
  
  const { data: pkg = null, isLoading } = useQuery({
    queryKey: ["company-package-access", currentCompanyId],
    queryFn: () => fetchCompanyPackage(currentCompanyId!),
    enabled: !!currentCompanyId,
  });

  return {
    isLoading,
    package: pkg,
    canCreateArticles: Boolean(pkg) && !pkg?.is_trial && (pkg?.is_enterprise || pkg?.max_articles_per_month == null || pkg.max_articles_per_month > 0),
    canCreateWebsites: Boolean(pkg) && !pkg?.is_trial && (pkg?.is_enterprise || pkg?.max_websites == null || pkg.max_websites > 0),
    canCreateMedia: Boolean(pkg) && !pkg?.is_trial && (pkg?.is_enterprise || pkg?.max_media_assets == null || pkg.max_media_assets > 0),
    canAddTeamMembers: Boolean(pkg) && !pkg?.is_trial && (pkg?.max_team_members ?? 0) > 1,
    articleLimit: pkg?.max_articles_per_month,
    websiteLimit: pkg?.max_websites,
    mediaLimit: pkg?.max_media_assets,
    teamMemberLimit: pkg?.max_team_members,
  };
}
